import { PayloadAction } from "@reduxjs/toolkit";
import { Ref, RefSource } from "../types";
import { LineageState } from "./types";

/**
 * ERD-overlay slice fragment: initial state and reducers spread into
 * `lineageSlice` to keep `LineageSlice.ts` under the 250-line limit.
 * All fields operate on the shared `LineageState` shape.
 */
export const refsInitialState: Pick<
  LineageState,
  | "refs"
  | "showRefs"
  | "enabledRefSources"
  | "inferenceConfidenceThreshold"
  | "includeSourcesInInference"
  | "expandedTables"
> = {
  refs: {},
  // Off by default; persisted `getLineageSettings` may override per-user.
  showRefs: false,
  // `inferred` opt-in: heuristic refs are noisy until users explicitly enable.
  enabledRefSources: { test: true, contract: true, semantic: true, inferred: false },
  inferenceConfidenceThreshold: 0.6,
  includeSourcesInInference: false,
  expandedTables: {},
};

export const refsReducers = {
  setRefs: (state: LineageState, action: PayloadAction<Ref[]>) => {
    const next: Record<string, Ref> = {};
    for (const ref of action.payload) next[ref.id] = ref;
    state.refs = next;
  },
  setShowRefs: (state: LineageState, action: PayloadAction<boolean>) => {
    state.showRefs = action.payload;
  },
  setEnabledRefSources: (
    state: LineageState,
    action: PayloadAction<Record<RefSource, boolean>>
  ) => {
    state.enabledRefSources = action.payload;
  },
  setRefSourceEnabled: (
    state: LineageState,
    action: PayloadAction<{ source: RefSource; enabled: boolean }>
  ) => {
    state.enabledRefSources[action.payload.source] = action.payload.enabled;
  },
  setInferenceConfidenceThreshold: (state: LineageState, action: PayloadAction<number>) => {
    state.inferenceConfidenceThreshold = action.payload;
  },
  setIncludeSourcesInInference: (state: LineageState, action: PayloadAction<boolean>) => {
    state.includeSourcesInInference = action.payload;
  },
  setAllTablesExpanded: (
    state: LineageState,
    action: PayloadAction<{ tables: string[]; expanded: boolean }>
  ) => {
    const { tables, expanded } = action.payload;
    for (const t of tables) {
      if (expanded) state.expandedTables[t] = true;
      else delete state.expandedTables[t];
    }
  },
};
